import { CheckoutSettings, WhiteLabelInfo } from "./../../core/models/model";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PaymentService } from "../shared/services/payment.service";
import { PaymentDetails } from "../shared/models/paymentdetails";
import { StorageService } from "src/app/core/helpers/storage-Service";
import { AuthService } from "src/app/core/auth/auth.service";
import { environment } from "../../../environments/environment";
import { CreatePaymentReqest } from "../shared/models/makepaymentReq";
import { DataService } from "../shared/data.service";
import {
  catchError,
  flatMap,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { EMPTY, Observable, of, Subject } from "rxjs";
import { TitleService } from "src/app/core/services/title.service";
import { LoadingService } from "src/app/core/services/loading.service";
import { ResponseCodes } from "../shared/models/bankPaymentRes";
import posthog from "posthog-js";

enum PaymentMethods {
  CARD = "Card",
  MOMO = "MOMO",
  BANK = "Bank",
  USSD = "USSD",
  MGPS = "MGPS",
  QR = "qr",
  OPAY = "OPAY",
  MONO = "MONO",
}

@Component({
  selector: "app-page-layout",
  templateUrl: "./page-layout.component.html",
  styleUrls: ["./page-layout.component.scss"],
  standalone: false,
})
export class PageLayoutComponent implements OnInit, OnDestroy {
  readonly PaymentMethods = PaymentMethods;
  selectedPaymentMethod: string;
  paymentReference: string;
  destroy$: Subject<boolean> = new Subject();

  accountDetails: any;
  paymentInfo: CheckoutSettings;

  whiteLabelInfo: WhiteLabelInfo;
  showErrorView: boolean;

  errorMessage: string;

  paymentDetails: Partial<PaymentDetails>;
  paymentDetialsCopy: PaymentDetails;
  isPageLoading: boolean = true;
  activity: string = "Processing Payment";
  currentActiveHeader = "";
  createPaymentReq = new CreatePaymentReqest();
  rand: number;

  private channelSwitch$ = new Subject<string>();
  //the channel being created right now, which is not yet the one on screen
  private pendingMethod: string;
  private scopedLoaderEnabled: boolean = false;
  channelErrorVisible: boolean = false;
  channelErrorMessage: string;

  amountWithFees: any;
  customerName: string;

  clientId: string;
  defaultPayment: string;

  baseAmount: number;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private payserv: PaymentService,
    private storage: StorageService,
    private auth: AuthService,
    private dserv: DataService,

    private title: TitleService,
    private loadingService: LoadingService,
  ) {
    this.paymentReference = route.snapshot.params["payRef"];
  }

  ngOnInit() {
    this.listenForChannelSwitch();

    this.getPaymentDetails(this.paymentReference);

    // const paymentMethods = this.storage.getPaymentInfo();

    this.setBaseUrl();
    // this.getAccessToken();
    this.getRand();

    // this.checkPaymentStatus('');
  }

  //a switch is only applied once the backend has the payment on the new channel.
  //switchMap so a quick double-click drops the stale response and the last click wins,
  //and catchError sits inside it so one failure does not kill the stream
  private listenForChannelSwitch() {
    this.channelSwitch$
      .pipe(
        switchMap((method) =>
          this.createPaymentForChannel(method).pipe(
            map((res) => ({ method, res })),
            catchError((error) => {
              this.showChannelError(error);
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ method, res }) => this.applyChannel(method, res));
  }

  // call this once after payment details are loaded
  private initializePayment(data: PaymentDetails) {
    const defaultMethod =
      this.paymentInfo?.paymentMethodInfo?.defaultChannel || "CARD";

    this.createPaymentForChannel(defaultMethod)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.applyChannel(defaultMethod, res),
        error: (error) => {
          this.isPageLoading = false;
          this.showChannelError(error);
        },
      });
  }

  //the sidebar labels and the paymentChannel the API expects are not the same thing
  private toPaymentChannel(method: string): string {
    switch (method) {
      case "ACCOUNT TRANSFER":
        return "ACCOUNT";
      case "OPAY":
        return "BANK";
      default:
        return method; // CARD, USSD, BANK
    }
  }

  //reuses the existing reference. getRand()/createPayment() below is the separate
  //retry flow that mints a brand new one
  private createPaymentForChannel(method: string) {
    const details = this.paymentDetails;

    return this.payserv.createPayment({
      reference: details.referenceId,
      clientId: details.clientId,
      amount: this.baseAmount,
      userId: details.userId,
      paymentChannel: this.toPaymentChannel(method),
      currency: details.currency,
      metadata: details["metadata"],
    });
  }

  private applyChannel(method: string, res: any) {
    this.paymentDetails.fees = res["fees"];
    this.amountWithFees = this.baseAmount + +res["fees"];
    this.selectedPaymentMethod = method;
    this.pendingMethod = undefined;
    this.isPageLoading = false;

    this.enableScopedLoader();
  }

  //from here on the card is on screen, so loading belongs inside the content
  //column rather than over the whole viewport. deferred because the
  //interceptor's finalize/stopLoading runs after this subscriber, and flipping
  //synchronously would swap full screen for boxed for a frame
  private enableScopedLoader() {
    if (this.scopedLoaderEnabled) return;
    this.scopedLoaderEnabled = true;

    setTimeout(() => this.loadingService.useScopedLoader(true));
  }

  //leaves selectedPaymentMethod alone so the current channel stays on screen
  private showChannelError(error) {
    this.pendingMethod = undefined;
    this.channelErrorMessage =
      error?.error?.responseMessage ||
      "Unable to switch payment method, please try again";
    this.channelErrorVisible = true;
  }

  // creates the payment on the selected channel, then switches the view
  setSelectedPaymentMethodPage(
    selectedMethod: string,
    source: "auto" | "user" = "user",
  ) {
    if (!this.paymentDetails) return;

    //compare against the in flight choice, not the one on screen. clicking away
    //and straight back would otherwise let the superseded switch land
    const current = this.pendingMethod ?? this.selectedPaymentMethod;

    //also covers the header emitting its default on init, right after
    //initializePayment already created the payment on that same channel
    if (selectedMethod === current) return;

    posthog.capture("payment_method_selected", {
      method: selectedMethod,
      source,
      paymentChannel: this.toPaymentChannel(selectedMethod),
      clientId: this.paymentDetails.clientId,
      amount: this.paymentDetails.amount,
      currency: this.paymentDetails.currency,
    });

    this.pendingMethod = selectedMethod;
    this.channelSwitch$.next(selectedMethod);
  }

  validateUrl() {
    //ensure to check that payment reference exist..
    this.activity = "Initializing...";
    this.isPageLoading = true;

    if (this.router.url.includes("bank")) {
      this.isPageLoading = false;
      this.bankRedirectedMessage();
      return;
    } else {
      this.getPaymentDetails(this.paymentReference);
    }

    // }
  }

  //this uses the paymentLink to get the actual payment details that is then use accross the checkout page
  getPaymentDetails(paymentReference) {
    this.payserv
      .getPaymentDetails(paymentReference)
      .pipe(
        mergeMap((val) =>
          this.payserv.getCheckoutSettings(val.clientId).pipe(
            map((res) => {
              val["checkoutSettings"] = res;
              return val;
            }),
          ),
        ),

        // Ensure access token exists before continuing
        mergeMap((data: PaymentDetails) => {
          const token = this.storage.getAccessToken();

          if (token) {
            return of(data);
          }

          return this.getCheckoutToken(
            "payment",
            data.clientId,
            data.referenceId,
          ).pipe(map(() => data));
        }),
      )
      .subscribe(
        (data: PaymentDetails) => {
          this.paymentDetails = data;
          this.storage.storePaymentData(data["checkoutSettings"]);
          this.paymentInfo = this.storage.getPaymentInfo();

          this.selectedPaymentMethod =
            this.paymentInfo?.paymentMethodInfo?.defaultChannel || "CARD";

          this.clientId = data.clientId;
          this.whiteLabelInfo = data["whiteLabelInfo"];
          this.customerName = data["metadata"].customerName;

          if (this.whiteLabelInfo) {
            this.dserv.emitWhiteLabelInfo.emit(this.whiteLabelInfo);
            this.storage.saveWhiteLabelInfo(this.whiteLabelInfo);
            this.storage.saveCustomerName(this.customerName);

            if (this.whiteLabelInfo.checkoutTitlePage) {
              this.title.updateTitle(
                this.whiteLabelInfo.checkoutTitlePage + " Checkout",
              );
            }

            const link: HTMLLinkElement =
              document.querySelector("link[rel*='icon']") ||
              (document.createElement("link") as HTMLLinkElement);

            link.type = "image/x-icon";
            link.rel = "icon";
            link.href = this.whiteLabelInfo.logoUrl;

            document.getElementsByTagName("head")[0].appendChild(link);
          }

          this.baseAmount = data.amount;

          if (
            this.paymentInfo?.feeBearer === "Customer" ||
            data.feeBearer === "Customer"
          ) {
            this.amountWithFees = this.baseAmount + +data.fees;
          }

          this.storage.saveClientDetails(data.clientName, data.clientId);

          // Token now exists, so this call is safe
          this.initializePayment(data);

          posthog.group("client", data.clientId, {
            name: data.clientName,
          });

          posthog.capture("payment_page_loaded", {
            clientId: data.clientId,
            amount: this.baseAmount,
            currency: data.currency,
            defaultChannel:
              this.paymentInfo?.paymentMethodInfo?.defaultChannel || "CARD",
          });

          // this.selectedPaymentMethod =
          //   data.country === "GHA" ? PaymentMethods.MGPS : PaymentMethods.CARD;
        },
        (error) => {
          if (error?.error?.responseMessage === "Payment already completed") {
            if (this.paymentDetails === undefined) {
              this.paymentDetails = {};
              this.paymentDetails.amount = 0;
              this.paymentDetails.referenceId = this.getRand();

              this.router.navigate([`/payment/completed`]);
            }
          } else if (
            error.error.responseMessage ===
            "User Account not active, Contact the Administrator"
          ) {
            this.showErrorView = true;
            this.errorMessage = error.error.responseMessage;
          } else if (
            error.error.responseCode === ResponseCodes.resourceNotFound
          ) {
            this.showErrorView = true;
            this.errorMessage = "The referenceId not found";
          } else {
            this.paymentDetails = {};
            this.paymentDetails.amount = 0;
          }
        },
      );
  }

  // get the client ref and client
  //get payment details
  //extract the actual paymentlink and get the payment details
  //navigate to the original url of the payment (not needed though)
  bankRedirectedMessage() {
    this.isPageLoading = true;
    let clientRef = this.route.snapshot.params["client"];
    let TxnRef = this.route.snapshot.params["reference"];
    this.payserv.checkPaymentStatus(clientRef, TxnRef).subscribe(
      async (data: any) => {
        this.paymentDetails = await data;
        this.paymentDetails.referenceId = data["referenceId"];

        this.checkOptionsStatus("bank");
        this.isPageLoading = false;
        return;
      },
      (error) => {
        this.router.navigate([
          `/failure/${this.paymentDetails.amount}/${
            this.paymentDetails.referenceId
          }/${btoa(this.paymentDetails.callbackURL)}`,
        ]);
        this.isPageLoading = false;
      },
    );
  }

  setLoader(value) {
    this.isPageLoading = value;
  }

  checkOptionsStatus(event) {
    if (event == "ussd") {
      this.currentActiveHeader = "ussd";
      return;
    }
    if (event == "card") {
      this.currentActiveHeader = "card";
      return;
    }
    if (event == "mpgs") {
      this.currentActiveHeader = "mpgs";
      return;
    }
    if (event == "bank") {
      this.currentActiveHeader = "bank";
      return;
    } else {
      this.currentActiveHeader = "";
      this.createPayment();
    }
  }

  //this is called when there is need to retry a payment.
  //new payment link is generated
  createPayment() {
    this.setCreatePaymentItems(this.paymentDetails);

    this.payserv.createPayment(this.createPaymentReq).subscribe(
      async (data) => {
        this.paymentDetialsCopy.paymentUrl = data["paymentUrl"];
        let a = await data["paymentUrl"].substring(
          data["paymentUrl"].indexOf(".com"), //on prod. just change the ip to .com
        );
        a = a.substring(4);
        if (!environment.production) {
          // for production
          a = a.substring(5);
        }
        // a = a.substring(4);
        this.router.navigate([`${a}`]);
        this.paymentDetails = this.paymentDetialsCopy;
      },
      (error) => {
        this.router.navigate([
          `/failure/${this.paymentDetails.amount}/${
            this.paymentDetails.referenceId
          }/${btoa(this.paymentDetails.callbackURL)}`,
        ]);
        this.isPageLoading = false;
      },
    );
  }

  setCreatePaymentItems(details) {
    this.createPaymentReq.amount = details.amount;
    this.createPaymentReq.clientId = details.clientId;
    this.createPaymentReq.currency = details.currency;
    this.createPaymentReq.userId = details.userId;
    // this.createPaymentReq.reference = this.getRand();
    this.createPaymentReq.reference = this.getRand();
    this.createPaymentReq.metadata = details["metadata"];
    this.paymentDetialsCopy = JSON.parse(
      JSON.stringify(this.paymentDetails), //this is used to remove the reference binding... so that ngOnchanges can catch recent changes instead of making call to get payment details
    );
    this.paymentDetialsCopy.referenceId = this.createPaymentReq.reference;
  }

  //reset the new link, and update Payment details
  //this is currently no in use becuase we are dereferencing paymentDetails and reassigning values
  //if there is need to request for details, you can den implement this
  resetPageHeader(newReference) {
    this.payserv.getPaymentDetails(newReference).subscribe(
      async (data: PaymentDetails) => {
        this.paymentDetails = await { ...data };
      },
      (error) => {
        this.router.navigate([
          `/failure/${this.paymentDetails.amount}/${
            this.paymentDetails.referenceId
          }/${btoa(this.paymentDetails.callbackURL)}`,
        ]);
        this.isPageLoading = false;
      },
    );
  }

  getRand() {
    this.rand = Math.floor(Math.random() * 900);
    return `${Date.now().toString()}${this.rand}`;
  }

  getAccessToken() {
    this.isPageLoading = true;
    if (!this.storage.getAccessToken()) {
      (this.auth.getAccessControl().subscribe(async (data) => {
        let dataToStore = await data;
        // if (this.storage.saveAccessToken(dataToStore)) {
        //   await this.validateUrl();
        // }
      }),
        (error) => {
          this.router.navigate([
            `/failure/${this.paymentDetails.amount}/${
              this.paymentDetails.referenceId
            }/${btoa(this.paymentDetails.callbackURL)}`,
          ]);
          this.isPageLoading = false;
          // this.isPageLoading = false;
        });
    } else {
      this.validateUrl();
    }
  }

  solveError(error) {
    if (error.error.responseMessage == "Payment already completed ") {
      this.router.navigate([`/failure/N0.00/None/${atob(error.error)}`]);
      return;
    }
  }

  handleCustomerResponse(e) {
    if (e === true) {
      // this.setPage("Card");
    }
  }

  setBaseUrl() {
    if (location.href.includes("/gh/")) {
      this.dserv.setBaseUrlState(environment.ghurl);
    } else {
      this.dserv.setBaseUrlState(environment.url);
    }
  }

  getCheckoutSettings(clientId: string) {
    this.payserv
      .getCheckoutSettings(clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.storage.storePaymentData(res);
        },
      });
  }

  // change this method to return Observable instead of subscribing
  getCheckoutToken(
    entityType?: string,
    clientId?: string,
    reference?: string,
  ): Observable<any> {
    return this.auth.getAccessControl(entityType, clientId, reference).pipe(
      tap((res) => {
        this.storage.saveAccessToken(res["token"]);
      }),
    );
  }

  ngOnDestroy(): void {
    //leaving this set would suppress the full screen loader app wide
    this.loadingService.useScopedLoader(false);

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
